import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
} from "@mui/material";
import dayjs from "utils/dayjs";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";
import VuiProgress from "components/VuiProgress";
import VuiTooltip from "components/VuiTooltip";
import VuiInput from "components/VuiInput";
import { FaCheckCircle, FaCrown, FaEdit, FaPlus, FaRobot, FaTimes } from "react-icons/fa";
import useCommon from "hooks/useCommon";
import usePermissions from "hooks/usePermissions";
import { getArrivalDetails } from "store/slice/arrivalsSlice";
import OverlayLoader from "components/OverlayLoader";
import { FaWandMagicSparkles } from "react-icons/fa6";
import { RiSparkling2Line } from "react-icons/ri";
import { saveArrivalUpdates } from "store/slice/arrivalsSlice";
import { preferenceLabels } from "variables";
import { resetSaveArrivalState } from "store/slice/arrivalsSlice";
import { generateReservationSummary } from "store/slice/arrivalsSlice";
import { clearArrivalDetails } from "store/slice/arrivalsSlice";
import { formatMissingLabel } from "utils/objectHelpers";
import useHotelDateTime from "hooks/useHotelDateTime";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

const { black, dark, primary, success, light } = colors;
const { borderWidth } = borders;

const ArrivalDetailsDialog = ({ open, reservationId, onSuccess, onClose, mode = "arrival" }) => {
  if (!reservationId) return null;

  const { DISPATCH, STORE } = useCommon();
  const { can } = usePermissions();
  const { formatDate, formatTime } = useHotelDateTime();
  const {
    arrivalDetails,
    detailsLoading,
    saveLoading,
    saveError,
    saveSuccess,
    summaryGenerating,
    summaryError,
  } = STORE((state) => state.arrivals);
  const isArrivalMode = mode === "arrival";

  const [checklist, setChecklist] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [notes, setNotes] = useState("");
  const [originalNotes, setOriginalNotes] = useState("");
  const [addedItems, setAddedItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const [toggledItems, setToggledItems] = useState([]);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [refreshingAfterSave, setRefreshingAfterSave] = useState(false);

  const reservation = arrivalDetails?.reservation;
  const aiSummary = arrivalDetails?.aiSummary?.summaryText;
  const generationCount = arrivalDetails?.aiSummary?.generationCount || 0;
  const maxSummaryReached = generationCount >= 5;
  const guest = arrivalDetails?.guest;
  const preference = arrivalDetails?.preference;
  const guestCount = reservation?.guestCounts?.total || 0;
  const adults = reservation?.guestCounts?.adults;
  const children = reservation?.guestCounts?.children;
  const roomNumber = reservation?.roomNumber || "Unassigned";

  const pmsNote = useMemo(() => {
    if (!arrivalDetails?.notes?.length) return null;

    return arrivalDetails.notes
      .filter(
        (n) =>
          n.visibility === "pms" &&
          (n.category?.toLowerCase() === "pms_note" || n.pmsType === "pms_note") &&
          !n.isDeleted
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  }, [arrivalDetails]);

  const arrivalDate = reservation?.checkInDate ? formatDate(reservation.checkInDate) : "—";

  const arrivalTime = reservation?.checkInDate ? formatTime(reservation.checkInDate) : "";

  const checkoutDate = reservation?.checkOutDate ? formatDate(reservation.checkOutDate) : "—";

  const stayPeriod =
    reservation?.checkInDate && reservation?.checkOutDate
      ? `${arrivalDate} → ${checkoutDate}`
      : "—";

  const completedGroups = useMemo(() => {
    return (
      preference?.grouped?.completed
        ?.filter((group) => group.category === "About Your Stay")
        .map((group) => {
          const filteredItems = group.items.filter((item) => {
            // Skip pet row if pet = false / not provided
            if (item.questionKey === "has_pet" && !item.value?.yes) {
              return false;
            }

            return true;
          });

          return {
            ...group,
            items: filteredItems,
          };
        })
        .filter((group) => group.items.length > 0) || []
    );
  }, [preference]);

  const missingGroups = useMemo(() => {
    return (
      preference?.grouped?.missing?.filter((group) => group.category === "About Your Stay") || []
    );
  }, [preference]);

  const completedCount = useMemo(
    () => completedGroups.reduce((total, group) => total + group.items.length, 0),
    [completedGroups]
  );

  const missingCount = useMemo(
    () => missingGroups.reduce((total, group) => total + group.items.length, 0),
    [missingGroups]
  );

  const progress = useMemo(() => {
    const completed = checklist.filter((i) => i.done).length;
    return Math.round((completed / checklist.length) * 100);
  }, [checklist]);

  const handleAddChecklistItem = () => {
    if (!newItem.trim()) return;

    const newChecklistItem = {
      id: `custom-${Date.now()}`,
      label: newItem.trim(),
      done: false,
    };

    setChecklist((prev) => [...prev, newChecklistItem]);

    setAddedItems((prev) => [...prev, { title: newChecklistItem.label }]);

    setNewItem("");
  };

  const handleRemoveChecklistItem = (id) => {
    setChecklist((prev) => prev.filter((item) => item.id !== id));

    setRemovedItems((prev) => [...prev, { itemId: id }]);
  };

  const toggleChecklist = (id) => {
    if (!id || !can("editArrivalDeparture")) return;

    setChecklist((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, done: !item.done };

          setToggledItems((prevToggle) => [
            ...prevToggle.filter((t) => t.itemId !== id),
            {
              itemId: id,
              isChecked: updated.done,
            },
          ]);

          return updated;
        }
        return item;
      })
    );
  };

  const handleSaveUpdates = async () => {
    try {
      DISPATCH(resetSaveArrivalState());

      await DISPATCH(
        saveArrivalUpdates({
          reservationId,
          payload: {
            noteText: notes || null,
            checklist: {
              add: addedItems,
              toggle: toggledItems,
              remove: removedItems,
            },
            preferenceCategories: ["arrival", "room", "sleep", "food", "pets"],
            questionnaireVersion: 1,
          },
        })
      ).unwrap();

      // Refresh details to display the saved notes and checklist.
      await DISPATCH(
        getArrivalDetails({
          reservationId,
          payload: {
            preferenceCategories: ["arrival", "room", "sleep", "pets"],
            questionnaireVersion: 1,
            includeNotes: true,
            notesLimit: 10,
          },
        })
      ).unwrap();

      // reset local state
      setAddedItems([]);
      setRemovedItems([]);
      setToggledItems([]);
      setNewItem("");
      setOriginalNotes(notes);
      setIsEditingNotes(false);

      // refresh table
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Save arrival updates failed:", error);
    }
  };
  const handleGenerateSummary = async () => {
    try {
      await DISPATCH(
        generateReservationSummary({
          reservationId,
        })
      ).unwrap();

      // refresh details
      // await DISPATCH(
      //   getArrivalDetails({
      //     reservationId,
      //     payload: {
      //       preferenceCategories: ["arrival", "room", "sleep", "pets"],
      //       questionnaireVersion: 1,
      //       includeNotes: true,
      //       notesLimit: 10,
      //     },
      //   })
      // );
    } catch (err) {
      console.error("AI summary generation failed:", err);
    }
  };
  const getProgressColor = (value) => {
    if (value >= 80) return "success";
    if (value >= 50) return "info";
    return "warning";
  };

  useEffect(() => {
    if (!reservationId) return;

    DISPATCH(resetSaveArrivalState());

    DISPATCH(
      getArrivalDetails({
        reservationId,
        payload: {
          preferenceCategories: ["arrival", "room", "sleep", "pets"],
          questionnaireVersion: 1,
          includeNotes: true,
          notesLimit: 10,
        },
      })
    );
  }, [reservationId, DISPATCH, open]);

  useEffect(() => {
    if (!reservation?.prepChecklistItems) return;

    const formattedChecklist = reservation.prepChecklistItems
      .filter((item) => !item.isDeleted)
      .map((item) => ({
        id: item._id,
        label: item.title,
        done: item.isChecked,
      }));

    setChecklist(formattedChecklist);
  }, [reservation]);

  useEffect(() => {
    if (!arrivalDetails?.notes) return;

    const latestNote = arrivalDetails.notes
      ?.filter((n) => n.category === "arrival_note" && n.visibility === "staff" && !n.isDeleted)
      ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

    const noteText = latestNote?.text || "";

    setNotes(noteText);
    setOriginalNotes(noteText);
  }, [arrivalDetails]);

  const hasChanges =
    addedItems.length || removedItems.length || toggledItems.length || notes.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <OverlayLoader
        open={detailsLoading || saveLoading}
        message={
          saveLoading
            ? "Saving updates..."
            : `Loading ${isArrivalMode ? "arrival" : "departure"}  details...`
        }
      />

      <DialogTitle>
        <VuiTypography>{isArrivalMode ? "Arrival Details" : "Departure Details"}</VuiTypography>
      </DialogTitle>

      <DialogContent
        sx={{
          p: { xs: 0, md: 2 },
        }}
      >
        {/* GUEST SUMMARY CARD */}
        <VuiBox
          mb={2}
          p={{ xs: 1, md: 2 }}
          borderRadius="16px"
          sx={{
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            border: "1px solid rgba(255,255,255,0.14)",
          }}
        >
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography variant="h6" fontWeight="regular">
              Guest Summary
            </VuiTypography>

            {guest?.loyaltyTier && (
              <VuiBox
                px={1.5}
                py={0.25}
                borderRadius="md"
                display="inline-flex"
                alignItems="center"
                gap={0.5}
                sx={({ palette: { info, white } }) => ({
                  background: info.main,
                  color: white.main,
                  fontSize: "12px",
                })}
              >
                <FaCrown size={12} color="#FFD700" sx={{ mr: 5 }} />
                {`${arrivalDetails.guest.loyaltyTier} Member`}
              </VuiBox>
            )}
          </VuiBox>

          <VuiBox
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            columnGap={2}
            rowGap={0}
          >
            <VuiBox>
              <VuiTypography variant="caption">Guest Name</VuiTypography>
              <VuiTypography variant="body2">
                {guest ? `${guest.firstName} ${guest.lastName}` : "—"}
              </VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption">Nationality</VuiTypography>
              <VuiTypography variant="body2">{guest?.nationality || "—"}</VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption">Language</VuiTypography>
              <VuiTypography variant="body2">{guest?.language?.toUpperCase() || "—"}</VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption">Phone Number</VuiTypography>
              <VuiTypography variant="body2">{guest?.phone || "—"}</VuiTypography>
            </VuiBox>

            {/* <VuiBox>
              <VuiTypography variant="caption" >
                Email
              </VuiTypography>
              <VuiTypography variant="body2">
                {guest?.email || "—"}
              </VuiTypography>
            </VuiBox> */}

            <VuiBox>
              <VuiTypography variant="caption">Arrival</VuiTypography>
              <VuiTypography variant="body2">
                {arrivalDate} • {arrivalTime}
              </VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption">Stay Period</VuiTypography>
              <VuiTypography variant="body2">{stayPeriod}</VuiTypography>
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="caption">Total Stays</VuiTypography>
              <VuiTypography variant="body2">{arrivalDetails?.totalStays || 0}</VuiTypography>
            </VuiBox>

            {preference?.specialOccasion && (
              <VuiBox display="flex" flexDirection="column" mt={2}>
                <VuiTypography variant="caption" mb={2}>
                  Special Occasion
                </VuiTypography>

                <VuiBox
                  width="fit-content"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  sx={({ palette: { success, white } }) => ({
                    background: success.main,
                    color: white.main,
                    fontSize: "12px",
                    lineHeight: 1.4,
                  })}
                >
                  <FaCheckCircle size={12} />
                  {preference?.specialOccasion}
                </VuiBox>
              </VuiBox>
            )}
          </VuiBox>

          <VuiBox mt={1.5} pt={0} borderTop="1px solid rgba(42, 42, 42, 0.12)">
            <VuiTypography variant="caption" sx={{ letterSpacing: "0.04em" }}>
              Guest Profile Summary (AI)
            </VuiTypography>

            {summaryError && (
              <VuiBox
                mt={1}
                py={0.5}
                px={1.5}
                borderRadius="md"
                sx={{
                  background: "rgba(255, 0, 0, 0.08)",
                  border: "1px solid rgba(255, 0, 0, 0.25)",
                }}
              >
                <VuiTypography variant="caption" color="error">
                  {summaryError?.message || "AI summary generation failed. Please try again."}
                </VuiTypography>
              </VuiBox>
            )}

            {aiSummary ? (
              <VuiTypography
                variant="body2"
                mt={1}
                fontWeight="light"
                lineHeight={1.6}
                sx={{ fontSize: "14px", letterSpacing: "0.04em" }}
              >
                {aiSummary}
              </VuiTypography>
            ) : isArrivalMode && can("generateAiSummary") ? (
              <VuiBox display="flex" justifyContent="center" my={1}>
                <VuiButton
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<RiSparkling2Line size={12} />}
                  onClick={handleGenerateSummary}
                  disabled={summaryGenerating}
                >
                  {summaryGenerating ? "Generating..." : "Generate"}
                </VuiButton>
              </VuiBox>
            ) : (
              <VuiTypography
                variant="caption"
                mt={1}
                sx={{
                  display: "block",
                  opacity: 0.7,
                }}
              >
                No AI summary available for this guest.
              </VuiTypography>
            )}
            {/* REGENERATE ACTION */}
            {aiSummary && isArrivalMode && can("regenerateAiSummary") && (
              <span>
                <VuiTooltip
                  title={
                    maxSummaryReached
                      ? "Maximum regeneration limit reached (5/5)."
                      : `Regenerate summary (${generationCount}/5 used)`
                  }
                  placement="top"
                >
                  <VuiTypography
                    variant="caption"
                    mt={1.5}
                    sx={{
                      cursor: "pointer",
                      opacity: summaryGenerating || maxSummaryReached ? 0.45 : 0.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      width: "fit-content",
                      pointerEvents: "auto",
                    }}
                    onClick={
                      !summaryGenerating && !maxSummaryReached ? handleGenerateSummary : undefined
                    }
                  >
                    <RiSparkling2Line size={12} />
                    {summaryGenerating
                      ? "Generating..."
                      : maxSummaryReached
                        ? "Limit Reached"
                        : "Regenerate"}
                  </VuiTypography>
                </VuiTooltip>
              </span>
            )}
          </VuiBox>
        </VuiBox>

        <VuiBox mb={2} display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
          {/* STAY DETAILS */}
          <VuiBox
            p={2}
            borderRadius="16px"
            sx={{
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
          >
            <VuiTypography variant="h6" fontWeight="regular" mb={1}>
              Stay Details
            </VuiTypography>

            <VuiBox display="grid" gridTemplateColumns="1fr 1fr" columnGap={2} rowGap={0}>
              <VuiBox>
                <VuiTypography variant="caption">Room Number</VuiTypography>
                <VuiTypography variant="body2">{roomNumber}</VuiTypography>
              </VuiBox>

              <VuiBox>
                <VuiTypography variant="caption">Guest Count</VuiTypography>
                <VuiTypography variant="body2">
                  {(() => {
                    if (adults && children) {
                      return `${adults} Adult${adults !== 1 ? "s" : ""}, ${children} Child${
                        children !== 1 ? "ren" : ""
                      }`;
                    }

                    if (adults) {
                      return `${adults} Adult${adults !== 1 ? "s" : ""}`;
                    }

                    if (children) {
                      return `${children} Child${children !== 1 ? "ren" : ""}`;
                    }

                    return `${guestCount} Guest${guestCount !== 1 ? "s" : ""}`;
                  })()}
                </VuiTypography>
              </VuiBox>

              <VuiBox>
                <VuiTypography variant="caption">Rate Plan</VuiTypography>
                <VuiTypography variant="body2">{reservation?.ratePlanName || "—"}</VuiTypography>
              </VuiBox>

              {/* <VuiBox>
                <VuiTypography variant="caption">Reservation Origin</VuiTypography>
                <VuiTypography variant="body2">{reservation?.origin || "—"}</VuiTypography>
              </VuiBox> */}
            </VuiBox>
          </VuiBox>

          {/* PAYMENT DETAILS */}
          <VuiBox
            p={2}
            borderRadius="16px"
            sx={{
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
          >
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <VuiTypography variant="h6" fontWeight="regular">
                Payment Details
              </VuiTypography>

              <VuiBadge
                badgeContent={
                  reservation?.paymentStatus
                    ? reservation.paymentStatus.charAt(0).toUpperCase() +
                      reservation.paymentStatus.slice(1)
                    : "Unknown"
                }
                color={
                  reservation?.paymentStatus === "paid"
                    ? "success"
                    : reservation?.paymentStatus === "partial"
                      ? "info"
                      : "warning"
                }
                size="xs"
              />
            </VuiBox>

            <VuiBox display="grid" gridTemplateColumns="1fr 1fr" columnGap={2} rowGap={0}>
              <VuiBox>
                <VuiTypography variant="caption">Total Amount</VuiTypography>
                <VuiTypography variant="body2">
                  {reservation?.currency} {reservation?.totalAmount || 0}
                </VuiTypography>
              </VuiBox>

              <VuiBox>
                <VuiTypography variant="caption">Paid</VuiTypography>
                <VuiTypography variant="body2" color="success">
                  {reservation?.currency} {reservation?.paidAmount || 0}
                </VuiTypography>
              </VuiBox>

              <VuiBox>
                <VuiTypography variant="caption">Balance Due</VuiTypography>
                <VuiTypography variant="body2" color="primary">
                  {reservation?.currency} {reservation?.balanceDue || 0}
                </VuiTypography>
              </VuiBox>

              <VuiBox>
                <VuiTypography variant="caption">Deposit</VuiTypography>
                <VuiTypography variant="body2">
                  {reservation?.currency} {reservation?.depositAmount || 0}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </VuiBox>
        </VuiBox>

        {/* ROOM PREPARATION CHECKLIST */}
        {isArrivalMode && (
          <VuiBox my={2}>
            <VuiTypography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ fontSize: { xs: "17px", md: "18px" } }}
            >
              Room Preparation Checklist
            </VuiTypography>

            {/* Checklist Items */}
            <VuiBox mt={2}>
              {checklist.map((item) => {
                return (
                  <VuiBox
                    key={item.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                    sx={{
                      "&:hover .remove-btn": {
                        opacity: 1,
                      },
                    }}
                  >
                    <VuiBox display="flex" alignItems="center" gap={1}>
                      <Checkbox checked={item.done} onChange={() => toggleChecklist(item.id)} />

                      <VuiTypography
                        variant="caption"
                        color={item.done ? "success" : "primary"}
                        sx={{
                          opacity: item.done ? 1 : 0.85,
                          textDecoration: item.done ? "line-through" : "none",
                          textTransform: "capitalize",
                          fontSize: "14px",
                        }}
                      >
                        {item.label}
                      </VuiTypography>
                    </VuiBox>

                    {can("editArrivalDeparture") && (
                      <VuiButton
                        className="remove-btn"
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => handleRemoveChecklistItem(item.id)}
                        disabled={!can("editArrivalDeparture")}
                        sx={{
                          minWidth: "auto",
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                        }}
                      >
                        <FaTimes size={12} />
                      </VuiButton>
                    )}
                  </VuiBox>
                );
              })}
            </VuiBox>

            {/* Add New Checklist Item */}
            {can("editArrivalDeparture") && (
              <VuiBox mt={2} display="flex" alignItems="center" gap={1.5}>
                <VuiInput
                  gradient
                  placeholder="Add preparation item…"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddChecklistItem();
                  }}
                  inputProps={{ maxLength: 500 }}
                />

                <VuiButton
                  size="small"
                  variant="outlined"
                  color="primary"
                  disabled={!newItem.trim()}
                  onClick={handleAddChecklistItem}
                >
                  Add
                </VuiButton>
              </VuiBox>
            )}

            {/* Progress */}
            {checklist.length > 0 && (
              <VuiBox mt={2}>
                <VuiTypography variant="caption">Preparation Progress</VuiTypography>

                <VuiProgress value={progress} color={getProgressColor(progress)} />
              </VuiBox>
            )}
          </VuiBox>
        )}

        {/* PREFERENCES REVIEW */}
        {isArrivalMode && (
          <VuiBox my={2}>
            <VuiTypography
              variant="subtitle1"
              fontWeight="bold"
              mb={2}
              sx={{ fontSize: { xs: "17px", md: "18px" } }}
            >
              Arrival Preferences
            </VuiTypography>

            <VuiBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
              {/* COMPLETED */}
              <VuiBox
                p={2}
                borderRadius="14px"
                sx={{
                  background: "rgba(0, 200, 140, 0.08)",
                  border: "1px solid rgba(0, 200, 140, 0.25)",
                }}
              >
                <VuiBox display="flex" justifyContent="space-between" mb={1}>
                  <VuiTypography variant="body2" color="success" fontWeight="medium">
                    Completed
                  </VuiTypography>

                  <VuiTypography variant="caption" color="success">
                    {completedCount} items
                  </VuiTypography>
                </VuiBox>

                {completedGroups.length ? (
                  completedGroups.map((group, i) => (
                    <VuiBox key={i} mb={1}>
                      {/* CATEGORY  */}
                      {/* <VuiTypography
                        variant="caption"
                        color="success"
                        sx={{
                          opacity: 0.8,
                          display: "block",
                          mb: 1,
                          letterSpacing: "0.05em",
                        }}
                      >
                        {group.category}
                      </VuiTypography> */}

                      {/* ITEMS */}
                      {group.items.map((item, j) => {
                        const label = preferenceLabels[item.description] || item.description;
                        const value =
                          typeof item.value === "object"
                            ? Object.keys(item.value)
                                .filter((k) => item.value[k])
                                .join(", ")
                            : item.value;

                        return (
                          <VuiTypography
                            key={j}
                            variant="caption"
                            sx={{
                              display: "block",
                              mb: 0.8,
                              opacity: 0.9,
                            }}
                          >
                            <VuiBox
                              display="flex"
                              alignItems="center"
                              gap={1}
                              justifyContent="space-between"
                            >
                              <VuiTypography variant="caption">• {label}</VuiTypography>

                              <VuiTypography variant="caption" sx={{ opacity: 0.7 }}>
                                {value || "—"}
                              </VuiTypography>
                            </VuiBox>
                          </VuiTypography>
                        );
                      })}
                    </VuiBox>
                  ))
                ) : (
                  <VuiTypography variant="caption">No completed preferences yet.</VuiTypography>
                )}
              </VuiBox>

              {/* MISSING */}
              <VuiBox
                p={2}
                borderRadius="14px"
                sx={{
                  background: "rgba(255, 170, 0, 0.08)",
                  border: "1px solid rgba(255, 170, 0, 0.25)",
                }}
              >
                <VuiBox display="flex" justifyContent="space-between" mb={1}>
                  <VuiTypography variant="body2" color="primary" fontWeight="medium">
                    Missing
                  </VuiTypography>

                  <VuiTypography variant="caption" color="primary">
                    {missingCount} items
                  </VuiTypography>
                </VuiBox>

                {missingGroups.length ? (
                  missingGroups.map((group, i) => (
                    <VuiBox key={i} mb={1}>
                      {/* <VuiTypography
                        variant="caption"
                        color="primary"
                        sx={{ opacity: 0.8, display: "block", mb: 1 }}
                      >
                        {group.category}
                      </VuiTypography> */}

                      {group.items.map((item, j) => {
                        const { label, hint } = formatMissingLabel(item.description);

                        return (
                          <VuiTypography
                            key={j}
                            variant="caption"
                            sx={{ display: "block", mb: 0.8, opacity: 0.85 }}
                          >
                            <VuiBox key={j} display="flex" gap={1} justifyContent="space-between">
                              <VuiTypography variant="caption" sx={{ opacity: 0.9 }}>
                                • {label}
                              </VuiTypography>

                              <VuiTypography
                                variant="caption"
                                color="primary"
                                sx={{
                                  opacity: 0.8,
                                  // fontStyle: "italic",
                                }}
                              >
                                {hint}
                              </VuiTypography>
                            </VuiBox>
                          </VuiTypography>
                        );
                      })}
                    </VuiBox>
                  ))
                ) : (
                  <VuiTypography variant="caption">No missing preferences.</VuiTypography>
                )}
              </VuiBox>
            </VuiBox>
          </VuiBox>
        )}

        {/* PMS NOTE */}
        {pmsNote?.text && (
          <VuiBox mt={2}>
            <VuiTypography
              variant="subtitle1"
              fontWeight="bold"
              mb={1}
              sx={{ fontSize: { xs: "17px", md: "18px" } }}
            >
              PMS Note
            </VuiTypography>

            <VuiBox
              p={2}
              borderRadius="14px"
              sx={{
                background: "rgba(255,255,255,0.04)",
                border: `${borderWidth[1]} solid ${rgba(dark.main, 0.25)}`,
              }}
            >
              <VuiTypography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  lineHeight: 1.6,
                }}
              >
                {pmsNote.text}
              </VuiTypography>

              {/* optional metadata */}
              <VuiTypography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.6 }}>
                Synced from PMS
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        )}

        {/* INTERNAL NOTES */}
        {/* INTERNAL NOTES */}
        {isArrivalMode && (
          <VuiBox mt={2}>
            <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <VuiTypography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ fontSize: { xs: "17px", md: "18px" } }}
              >
                Arrival Note
              </VuiTypography>

              {can("editArrivalDeparture") && (
                <VuiButton
                  size="small"
                  variant="text"
                  color="primary"
                  startIcon={
                    isEditingNotes ? (
                      <FaTimes size={11} />
                    ) : notes ? (
                      <FaEdit size={11} />
                    ) : (
                      <FaPlus size={11} />
                    )
                  }
                  onClick={() => {
                    if (isEditingNotes) {
                      // Cancel → restore original value
                      setNotes(originalNotes);
                    }

                    setIsEditingNotes((prev) => !prev);
                  }}
                >
                  {isEditingNotes ? "Cancel" : notes ? "Edit" : "Add Note"}
                </VuiButton>
              )}
            </VuiBox>

            {isEditingNotes ? (
              <VuiInput
                gradient
                multiline
                rows={3}
                placeholder="Add internal notes for staff…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            ) : (
              <VuiBox
                p={2}
                borderRadius="14px"
                sx={{
                  background: "rgba(255,255,255,0.04)",
                  border: `${borderWidth[1]} solid ${rgba(dark.main, 0.25)}`,
                  maxHeight: "90px",
                }}
              >
                <VuiTypography
                  variant="body2"
                  sx={{
                    opacity: notes ? 0.9 : 0.6,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {notes || "No arrival note added yet."}
                </VuiTypography>
              </VuiBox>
            )}
          </VuiBox>
        )}
        {/* {isArrivalMode && (
          <VuiBox mt={2}>
            <VuiTypography
              variant="subtitle1"
              
              fontWeight="bold"
              mb={1}
              sx={{ fontSize: { xs: "17px", md: "18px" } }}
            >
              Arrival Note
            </VuiTypography>

            <VuiInput
              gradient
              multiline
              rows={3}
              placeholder="Add internal notes for staff…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={can("editArrivals") ? false : true}
            />
          </VuiBox>
        )} */}
      </DialogContent>

      {saveSuccess && !saveLoading && (
        <VuiBox
          role="status"
          mx={3}
          my={2}
          p={1}
          borderRadius="md"
          sx={{
            background: rgba(success.main, 0.08),
            border: `1px solid ${rgba(success.main, 0.25)}`,
          }}
        >
          <VuiTypography variant="caption" color="success">
            Changes saved successfully.
          </VuiTypography>
        </VuiBox>
      )}

      {saveError && !saveLoading && (
        <VuiBox
          my={2}
          p={1}
          borderRadius="md"
          sx={{
            background: "rgba(255, 0, 0, 0.08)",
            border: "1px solid rgba(255, 0, 0, 0.25)",
          }}
        >
          <VuiTypography variant="caption" color="error">
            {saveError?.message || "Failed to save updates. Please try again."}
          </VuiTypography>
        </VuiBox>
      )}

      {/* FOOTER */}
      <DialogActions sx={{ my: 1 }}>
        <VuiButton variant="outlined" color="primary" onClick={onClose} disabled={saveLoading}>
          Close
        </VuiButton>
        {isArrivalMode && can("editArrivalDeparture") && (
          <VuiButton
            variant="gradient"
            color="success"
            disabled={!hasChanges || saveLoading}
            onClick={handleSaveUpdates}
          >
            {saveLoading ? "Saving..." : "Save Updates"}
          </VuiButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ArrivalDetailsDialog;
